import MarketingNav from "@/components/Layout/MarketingNav";
import MarketingFooter from "@/components/Layout/MarketingFooter";
import { useTheme } from "@/lib/theme";
import {
  Workflow, Users, Briefcase, ClipboardList,
  ShieldCheck, BarChart3, LayoutGrid, Building2,
  Sparkles,
} from "lucide-react";

const PILLARS = [
  {
    i: Workflow,
    t: "Project Management",
    d: "Plan, assign and ship work without losing the thread. From the daily standup task to the quarterly company OKR — everything has an owner, a due date and a clear status.",
    rows: ["Plans, milestones & tasks", "Goals & quarterly reviews", "RAID logs & action items"],
  },
  {
    i: Users,
    t: "HRMS that fits your team",
    d: "An employee directory that's more than a contact list. Departments, roles, recognition and the basics of people-ops, all in the same workspace your team already lives in.",
    rows: ["Employee master & directory", "Departments & reporting lines", "Recognition & leaderboards"],
  },
  {
    i: Briefcase,
    t: "CRM, end to end",
    d: "From the first conversation with a prospect through to the final paid invoice — without the data ever leaving the building. One system of record for every customer.",
    rows: ["Leads & pipelines", "Quotations & sales orders", "Invoicing & collections"],
  },
  {
    i: ClipboardList,
    t: "Process efficiency",
    d: "Standardise the way your team works so quality doesn't drop with the next 20 hires. Documented procedures, structured handoffs and a calendar everyone can see.",
    rows: ["SOPs with acknowledgements", "Bulletin board & announcements", "Shared calendars"],
  },
];

const FOUNDATION = [
  { i: BarChart3, t: "Dashboards & reports", d: "Live rollups of work, revenue and team activity — decide on facts, not feel." },
  { i: LayoutGrid, t: "Client & partner portal", d: "Give external stakeholders a polished, scoped view of the work that's relevant to them." },
  { i: Building2, t: "Multi-workspace & branding", d: "Run multiple business units or brands side by side — each with its own look and rules." },
  { i: ShieldCheck, t: "Roles, audit & vault", d: "Granular permissions, audit trails and an encrypted credentials vault, built into the foundation." },
];

export default function Features() {
  const { branding } = useTheme();
  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-16">
        <div className="label-eyebrow mb-4">What&apos;s inside</div>
        <h1 className="heading-display text-5xl sm:text-7xl mb-6 max-w-4xl">
          Everything an MSME needs to run — minus the bloat.
        </h1>
        <p className="text-muted-foreground text-lg max-w-2xl mb-16">
          {branding.platform_name} is built around four pillars that cover the day-to-day operations of a growing business.
          Start with the one that&apos;s most painful today and switch on the rest as you scale.
        </p>

        {/* Pillars */}
        <div className="grid md:grid-cols-2 gap-3">
          {PILLARS.map((p, idx) => (
            <div key={idx} className="border border-border p-8 hover:border-primary/40 transition flex flex-col" data-testid={`features-pillar-${idx}`}>
              <p.i className="w-7 h-7 text-primary mb-5" />
              <h3 className="font-bold text-2xl mb-3" style={{ fontFamily: "var(--font-heading)" }}>{p.t}</h3>
              <p className="text-muted-foreground text-sm mb-6 leading-relaxed">{p.d}</p>
              <ul className="space-y-2 text-sm text-foreground/80 mt-auto">
                {p.rows.map((row, j) => (
                  <li key={j} className="flex gap-2 items-center" data-testid={`features-pillar-${idx}-row-${j}`}>
                    <span className="w-1 h-1 rounded-full bg-primary" />
                    {row}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* FOUNDATION */}
      <section className="border-t border-border/50 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
          <div className="label-eyebrow mb-4">The foundation</div>
          <h2 className="heading-display text-3xl sm:text-4xl max-w-3xl mb-12">
            Built on the boring-but-important things.
          </h2>
          <div className="grid md:grid-cols-4 gap-px bg-border border border-border">
            {FOUNDATION.map((f, idx) => (
              <div key={idx} className="bg-background p-8" data-testid={`features-foundation-${idx}`}>
                <f.i className="w-6 h-6 text-primary mb-4" />
                <h3 className="font-bold text-lg mb-2" style={{ fontFamily: "var(--font-heading)" }}>{f.t}</h3>
                <p className="text-sm text-muted-foreground">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEASER */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20 text-center">
        <Sparkles className="w-6 h-6 text-primary mx-auto mb-4" />
        <h2 className="heading-display text-3xl sm:text-4xl max-w-3xl mx-auto mb-4">
          And quite a bit more, depending on how you grow.
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          We&apos;ve kept the spotlight on the pillars that matter for most teams.
          The deeper toolkit reveals itself as you settle in — and as your operations ask for it.
        </p>
      </section>

      <MarketingFooter />
    </div>
  );
}
